//system
import { Injectable } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';

//models
import { Booking, MemberAccount, MemberCurrency } from 'src/app/models/member';

//services
import { HttpService } from './common/http.service';
import { AuthService } from './auth.service';
import { EnvironmentService } from './environment.service';
import { Pack } from '../models/meta';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  freeTrialDesc: string = 'Free trial';
  cachedMemberAccount$!: Observable<MemberAccount> | null;

  constructor(private _auth: AuthService, 
    private httpService: HttpService,
    private httpServiceInstance: HttpService,
    private _environmentService: EnvironmentService

  ) { 
    this.httpServiceInstance = this.httpService.createInstance({
      baseUrl: `${this._environmentService.getFacilityConfig().facilityUrl}api/v${this._environmentService.getFacilityConfig().facilityApiVersion}`
    });
  }

  fetchUser() {
    if (this._auth.isLoggedIn()) {
      return this.httpServiceInstance.get('Person/me');
    }
    return null;
  }

  fetchMember() {
    if (this._auth.isLoggedIn()) {
      return this.httpServiceInstance.get('Member/Info');
    }
    return null;
  }

  fetchAvatar(memberId: number) {
    if (this._auth.isLoggedIn()) {
      return this.httpServiceInstance.get(`Upload/ByIntent/1/${memberId}/member_default_photo`);
    }
    return null;
  }

  getUser() {
    return this._auth.getProp('user');
  }

  getMember() {
    return this._auth.getProp('member');
  }

  getPhotoUrl() {
    if (this._auth.isLoggedIn()) {
      let avatar = this._auth.getProp('avatar');
      if (avatar) {
        return avatar.UploadedUrl;
      }
      let user = this._auth.getProp('user');
      if(user)
        return `${user.FirstInitial}${user.LastInitial}`;

      return '/assets/images/df/profile.png';
    }
    return null;
  }

  fetchMemberAccount(force: boolean = false): Observable<MemberAccount> | null {
    if (this._auth.isLoggedIn()) {
      let member = this._auth.getProp('member');
      if (member?.Id) {
        if (!this.cachedMemberAccount$ || force) {
          this.cachedMemberAccount$ = this.httpServiceInstance.get<MemberAccount>(`Member/MemberAppNew/${member.Id}`).pipe(
            shareReplay({ bufferSize: 1, refCount: true })
          );
        }
        return this.cachedMemberAccount$;
      }
    }
    return null;
  }

  invalidateMemberAccountCache(): void {
    this.cachedMemberAccount$ = null;
  }

  getCurrentCycle(s: any) {
    let today = new Date();
    if (s.Cycles && s.Cycles.length) {
      for (let i in s.Cycles) {
        let cycle = s.Cycles[i];
        if (cycle && new Date(cycle.StartAt) <= today && new Date(cycle.EndAt) >= today) {
          return cycle;
        }
      }
    }
    return null;
  }

  /**
   * Soonest non-cancelled cycle that starts on or after today.
   * Used when prepaid renewals exist far ahead and there is no "current" cycle row.
   */
  getUpcomingCycle(s: any) {
    if (!s.Cycles?.length) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let best: any = null;
    let bestStart = Number.POSITIVE_INFINITY;

    for (const cycle of s.Cycles) {
      if (!cycle || cycle.IsCancelled) {
        continue;
      }
      const start = new Date(cycle.StartAt);
      if (start < today) {
        continue;
      }
      const t = start.getTime();
      if (t < bestStart) {
        bestStart = t;
        best = cycle;
      }
    }

    return best;
  }

  /** Furthest prepaid cycle — prefer {@link getCurrentCycle} / {@link getUpcomingCycle} for display. */
  getLastCycle(s: any) {
    if (s.Cycles && s.Cycles.length > 0) {
      let cycle = s.Cycles[1];
      for (let i in s.Cycles) {
        cycle = s.Cycles[i];
      }
      return cycle;
    }
    return null;
  }

  isCancelled(s: any) {
    let cycle = this.getCurrentCycle(s);
    if (cycle) return cycle.IsCancelled;
      return true;
  }

  isFreeTrial(s: any) {
    return s.Description === this.freeTrialDesc;
  }

  getMemberSubscriptions(memberAccount: MemberAccount | null): Booking[] {
    let myDrop = memberAccount?.MyDrop;
    return myDrop?.Subscriptions || [];
  }

  getMemberPacks(memberAccount: MemberAccount | null): MemberCurrency[] {
    let myDrop = memberAccount?.MyDrop;
    let packs = (myDrop?.Packs || []).filter((p: MemberCurrency) => {
      return new Date(p.EndAt).getTime() > Date.now();
    });
    return packs;
  }

  classesRemainingSub(subscriptions: Booking[], packs: Pack[]): string {
    let total = 0;
    for (const subscription of subscriptions) {
      const cycle = this.getCurrentCycle(subscription);
      if (!cycle) continue;
      for (const pack of packs) {
        if (pack.Id === cycle.PackId) {
          if (pack.IsUnlimited) 
            return 'Unlimited';
          total += parseInt(cycle.Balance, 10) || 0;
        }
      }
    }
    return total.toString();
  }

  classesRemainingPack(p: any): string {
    return p.Balance;
  }

  classesRemaining(subscriptions: any[], packs: any[]): string {
    let balance = 0;
    for (let i in subscriptions) {
      let sub = subscriptions[i];
      if (sub.CurrencyId !== 1) continue;
      if (sub.Description === this.freeTrialDesc) return 'Free Trial';
      let b = this.classesRemainingSub(sub, packs);
      if (b === 'Unlimited') return b;
      balance += parseInt(b, 10);
    }
    for (let i in packs) {
      let pack = packs[i];
      if (pack.CurrencyId !== 1) continue;
      balance += pack.Balance;
    }
    return balance.toString();
  }
}
